import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Route, Routes } from "react-router-dom";
import { Show, UserButton, SignOutButton } from "@clerk/react";
import { LayoutDashboard, Boxes, Search, Image, UserPen, FileText, Columns3, FolderTree, Scale, Mail, Inbox, Settings, Users, Menu, ExternalLink } from "lucide-react";
import Logo from "@/components/site/Logo";
import Seo from "@/components/site/Seo";
import { Loading } from "@/components/site/Ui";
import { useAdminApi } from "@/admin/useAdminApi";
import { EntityList, EntityEditor } from "@/admin/Entities";
import { AdminHome, Subscribers, Messages, SiteSettings, UsersPage, MediaLibrary, ContentSearch } from "@/admin/AdminPages";

const NAV = [
  ["", "Dashboard", LayoutDashboard], ["search", "Content search", Search], ["software", "Software", Boxes], ["articles", "Articles", FileText], ["comparisons", "Comparisons", Columns3],
  ["categories", "Categories", FolderTree], ["pages", "Legal pages", Scale], ["authors", "Authors", UserPen], ["media", "Media library", Image], ["subscribers", "Subscribers", Mail], ["messages", "Messages", Inbox],
  ["settings", "Settings", Settings], ["users", "Admins & users", Users],
];

function Gate({ children }) {
  const call = useAdminApi();
  const [me, setMe] = useState(null);
  useEffect(() => { call("get", "/me").then((r) => setMe(r.data)).catch(() => setMe({ role: "none" })); }, [call]);
  if (!me) return <Loading />;
  if (me.role !== "admin") return (
    <div className="min-h-screen grid place-items-center bg-[#F1F4F6] px-4" data-testid="admin-not-authorized">
      <div className="card p-8 max-w-md text-center">
        <h1 className="text-2xl font-bold">Not authorized</h1>
        <p className="mt-2 text-sm">This account does not have administrator access. Ask an existing administrator to grant access.</p>
        <div className="mt-6 flex justify-center gap-2"><Link to="/" className="btn-secondary">Back to site</Link><SignOutButton><button className="btn-primary" data-testid="admin-signout">Sign out</button></SignOutButton></div>
      </div>
    </div>
  );
  return children;
}

function Shell() {
  const [open, setOpen] = useState(false);
  const entities = ["software", "articles", "comparisons", "categories", "pages", "authors"];
  return (
    <div className="min-h-screen bg-[#F1F4F6]" data-testid="admin-dashboard">
      <Seo title="Admin" noindex />
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-[#DCE5E8] p-5 flex flex-col transition-transform lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <Logo testId="admin-logo" />
        <p className="mt-1 ml-11 text-[11px] font-semibold uppercase tracking-wider text-[#8A96A8]">Admin</p>
        <nav className="mt-8 space-y-1 flex-1 overflow-y-auto">
          {NAV.map(([to, label, Icon]) => (
            <NavLink key={label} to={`/admin${to ? `/${to}` : ""}`} end={!to} onClick={() => setOpen(false)} data-testid={`admin-nav-${to || "dashboard"}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-[#EDF2F4] text-[#3F5E7A]" : "text-[#17243A] hover:bg-[#F1F4F6]"}`}>
              <Icon className="h-4 w-4" />{label}
            </NavLink>
          ))}
        </nav>
        <a href="/" target="_blank" rel="noreferrer" className="btn-ghost btn-sm justify-start" data-testid="admin-view-site"><ExternalLink className="h-3.5 w-3.5" />View site</a>
      </aside>
      {open && <div className="fixed inset-0 bg-black/20 z-30 lg:hidden" onClick={() => setOpen(false)} />}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 h-14 bg-white/90 backdrop-blur border-b border-[#DCE5E8] px-4 sm:px-6 flex items-center justify-between">
          <button className="lg:hidden btn-ghost !p-2" onClick={() => setOpen(true)} aria-label="Open admin menu" data-testid="admin-menu-toggle"><Menu className="h-5 w-5" /></button>
          <span className="hidden lg:block text-sm text-[#64728A]">SaaSPivora content management</span>
          <UserButton />
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-6xl">
          <Routes>
            <Route index element={<AdminHome />} />
            {entities.map((e) => <Route key={e} path={e} element={<EntityList entity={e} />} />)}
            {entities.map((e) => <Route key={`${e}-ed`} path={`${e}/:id`} element={<EntityEditor key={e} entity={e} />} />)}
            <Route path="media" element={<MediaLibrary />} />
            <Route path="search" element={<ContentSearch />} />
            <Route path="subscribers" element={<Subscribers />} />
            <Route path="messages" element={<Messages />} />
            <Route path="settings" element={<SiteSettings />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

export default function AdminApp() {
  return (
    <>
      <Show when="signed-in"><Gate><Shell /></Gate></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </>
  );
}
